import {
  FieldWrapper,
  FormInputGroup,
  FormInputGroupLayout,
  FormValues,
  getValue,
  InputForm,
  InputFormComponent,
  InputFormGroup,
  Select,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import {
  DefaultButton,
  FocusZone,
  FocusZoneTabbableElements,
  IButtonStyles,
  mergeStyleSets,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { IntegerInput } from "../../components/IntegerInput/IntegerInput";
import { Settings, SettingsContext, SettingType } from "../../console/SettingsContext";
import { DetailsPanelId, InlineFormId, PageId } from "../../constants/pluginConstants";
import { footerHeight } from "../../constants/uiConstants";
import {
  CustomMinuteId,
  CustomMinutes,
  getCustomMinuteStr,
  getInactivityThresholdStr,
  InactivityThreshold,
  InactivityThresholdId,
  InactivityThresholds,
} from "../../helpers/settingsHelper";
import { useAnalytics } from "../../hooks/useAnalytics";

export const enum Fields {
  Inactivity = "inactivity",
  Hours = "hours",
  Minutes = "minutes",
}

export enum ButtonTestIds {
  ApplySettings = "apply-signout-settings",
  DiscardSettings = "discard-signout-settings",
}

const CUSTOM_GROUP_NAME = "custom";

const classNames = mergeStyleSets({
  actionFooter: {
    position: "fixed",
    padding: "10px 10px 0px 10px",
    bottom: `${footerHeight}px`,
    width: "100%",
    height: "44px",
  },
  buttons: {
    justifyContent: "flex-start",
    marginTop: 3,
  },
  inactivityThresholdIdSelectContainer: { width: "50%", minWidth: "107px", maxWidth: "530px" },
  customHrs: { width: "205px", minWidth: "205px" },
  customMins: { width: "60px", minWidth: "60px" },
});

const btnStyles: IButtonStyles = { root: { height: 24 } };

const getCustomMinuteId = (mins: number): string => {
  switch (mins) {
    case 45: return CustomMinuteId.CUSTOM_MINS_45;
    case 30: return CustomMinuteId.CUSTOM_MINS_30;
    case 15: return CustomMinuteId.CUSTOM_MINS_15;
    case 0:
    default: return CustomMinuteId.CUSTOM_MINS_0;
  }
};
export enum FieldTestIds {
  SignOutOptionDropdown = "signout-dropdown",
  CustomHoursInput = "hours-input",
  CustomMinutesDropdown = "minutes-dropdown",
}

const idleTime2HrsMins = (idleInMins: number): [number, number] => {
  const hrs = Math.floor(idleInMins / 60);
  const mins = idleInMins % 60;
  return [hrs, mins];
};

export const SettingSignout = (): JSX.Element => {
  const { trackFormSave } = useAnalytics();

  const formComponentRef = React.useRef<InputFormComponent>();
  const setFormComponentRef = (formComponent: InputFormComponent): void => {
    formComponentRef.current = formComponent;
  };

  const { inactivityThreshold, setSettingValue } = React.useContext<Settings>(SettingsContext);

  const [inactivityThresholdStorage, setInactivityThresholdStorage] = React.useState<InactivityThreshold | undefined>(
    inactivityThreshold,
  );

  const [inactivityThresholdId, setInactivityThresholdId] = React.useState<InactivityThresholdId>(
    inactivityThreshold.id,
  );
  const [inactivityKey, setInactivityKey] = React.useState<string>(uniqueGUID());

  const [customHours, setCustomHours] = React.useState<number | undefined>(
    inactivityThreshold.id === InactivityThresholdId.CUSTOM && inactivityThreshold.idleInMins
      ? Math.floor(inactivityThreshold.idleInMins / 60)
      : 0,
  );
  const [customHoursKey, setCustomHoursKey] = React.useState<string>(uniqueGUID());

  const [customMins, setCustomMins] = React.useState<number | undefined>(
    inactivityThreshold.id === InactivityThresholdId.CUSTOM && inactivityThreshold.idleInMins
      ? inactivityThreshold.idleInMins % 60
      : 0,
  );
  const [customMinsKey, setCustomMinsKey] = React.useState<string>(uniqueGUID());

  const [isButtonDisabled, setButtonDisabled] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!inactivityThresholdStorage) {
      setButtonDisabled(false);
    } else if (inactivityThresholdId === InactivityThresholdId.CUSTOM) {
      if (inactivityThresholdStorage.id === inactivityThresholdId) {
        const idleTime = idleTime2HrsMins(inactivityThresholdStorage.idleInMins || 0);
        setButtonDisabled(idleTime[0] === customHours && idleTime[1] === customMins);
      } else {
        setButtonDisabled(customHours === 0 && customMins === 0);
      }
    } else if (inactivityThresholdStorage.id !== inactivityThresholdId) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactivityThresholdId, customHours, customMins, JSON.stringify(inactivityThresholdStorage)]);

  const onClick = (): void => {
    if (!formComponentRef.current?.isValidationEnabled()) {
      formComponentRef.current?.enableValidation();
    }
    formComponentRef.current?.submit();
  };

  const onSubmit = (formValues: FormValues): void => {
    const thresholdId = getValue(formValues, Fields.Inactivity, InputFormGroup)?.[0].id;
    const threshold: InactivityThreshold = { id: thresholdId, idleInMins: undefined };
    if (thresholdId === InactivityThresholdId.CUSTOM) {
      const customHrs = parseInt(getValue(formValues, Fields.Hours, CUSTOM_GROUP_NAME) || 0, 10);
      const customMinId = getValue(formValues, Fields.Minutes, CUSTOM_GROUP_NAME)?.[0].id as CustomMinuteId
        || CustomMinuteId.CUSTOM_MINS_0;

      const { duration: customMin } = CustomMinutes[customMinId];
      const idleInMins = customHrs * 60 + customMin;
      threshold.idleInMins = idleInMins;
    } else {
      const { idleInMins } = InactivityThresholds[thresholdId as InactivityThresholdId];
      threshold.idleInMins = idleInMins;
    }
    setSettingValue(SettingType.INACTIVITY_THRESHOLD, threshold);
    setInactivityThresholdStorage(threshold);
  };

  const onCancel = (): void => {
    setInactivityThresholdId(inactivityThreshold.id);
    if (inactivityThreshold.id === InactivityThresholdId.CUSTOM && inactivityThreshold.idleInMins) {
      const idleTime = idleTime2HrsMins(inactivityThreshold.idleInMins);
      setCustomHours(idleTime[0]);
      setCustomMins(idleTime[1]);

      setCustomHoursKey(uniqueGUID());
      setCustomMinsKey(uniqueGUID());
    }
    setInactivityKey(uniqueGUID());
  };

  const optionItems = Object.values(InactivityThresholds).map(threshold => {
    const { id } = threshold;
    return ({ id, text: getInactivityThresholdStr(id) });
  });

  const minutesItems = Object.values(CustomMinutes).map(customMin => {
    const { id } = customMin;
    return ({ id, text: getCustomMinuteStr(id) });
  });

  return (
    <InputForm
      layout={FormInputGroupLayout.WIDE}
      componentRef={setFormComponentRef}
      onSubmit={onSubmit}
    >
      <Stack className={classNames.inactivityThresholdIdSelectContainer} tokens={{ childrenGap: 8 }}>
        <Text>{Messages.labels.signingOut()}</Text>
        <Select
          testId={FieldTestIds.SignOutOptionDropdown}
          key={inactivityKey}
          fieldName={Fields.Inactivity}
          label={Messages.labels.signOutInactive()}
          hideSearchBox
          options={optionItems}
          defaultValue={[inactivityThresholdId]}
          onChange={(thresholdId: string) => {
            setInactivityThresholdId(thresholdId as InactivityThresholdId);
            if (thresholdId !== InactivityThresholdId.CUSTOM) {
              setCustomHours(0);
              setCustomMins(0);
            }
          }}
        />
        {inactivityThresholdId === InactivityThresholdId.CUSTOM && (
          <FieldWrapper
            label={Messages.labels.customDuration()}
            subField
          >
            <FormInputGroup
              layout={FormInputGroupLayout.COMPACT}
              groupName={CUSTOM_GROUP_NAME}
            >
              <Stack horizontal tokens={{ childrenGap: 15 }}>
                <IntegerInput
                  testId={FieldTestIds.CustomHoursInput}
                  key={customHoursKey}
                  fieldName={Fields.Hours}
                  className={classNames.customHrs}
                  label={Messages.common.hours()}
                  min={0}
                  defaultValue={customHours}
                  onChange={(hours: number) => {
                    setCustomHours(hours);
                  }}
                />
                <Select
                  testId={FieldTestIds.CustomMinutesDropdown}
                  key={customMinsKey}
                  fieldName={Fields.Minutes}
                  className={classNames.customMins}
                  label={Messages.common.minutes()}
                  hideSearchBox
                  options={minutesItems}
                  // eslint-disable-next-line eqeqeq
                  defaultValue={customMins != undefined ? [getCustomMinuteId(customMins)] : undefined}
                  onChange={(minutesId: string) => {
                    const { duration } = CustomMinutes[minutesId as CustomMinuteId];
                    setCustomMins(duration);
                  }}
                />
              </Stack>
            </FormInputGroup>
          </FieldWrapper>
        )}
      </Stack>
      <footer className={classNames.actionFooter}>
        <FocusZone handleTabKey={FocusZoneTabbableElements.all} isCircularNavigation>
          <Stack className={classNames.buttons} tokens={{ childrenGap: 8 }} horizontal>
            <PrimaryButton
              disabled={isButtonDisabled}
              onClick={() => {
                trackFormSave(InlineFormId.SETTING_SIGNOUT, PageId.SETTINGS, DetailsPanelId.SIGNOUT);
                onClick();
              }}
              styles={btnStyles}
              data-test-id={ButtonTestIds.ApplySettings}
            >
              {Messages.actions.apply()}
            </PrimaryButton>
            <DefaultButton
              styles={btnStyles}
              disabled={isButtonDisabled}
              onClick={() => {
                onCancel();
              }}
              data-test-id={ButtonTestIds.DiscardSettings}
            >
              {Messages.actions.discardChanges()}
            </DefaultButton>
          </Stack>
        </FocusZone>
      </footer>
    </InputForm>
  );
};
